
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, User, LogOut, Users, Building } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useNavigation } from "@/hooks/use-navigation";

const Navbar = () => {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { isOpen, setIsOpen, scrollToSection } = useNavigation();

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        checkSubscription(session.user.id);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        checkSubscription(session.user.id);
      } else {
        setSubscription(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkSubscription = async (userId: string) => {
    try {
      const { data } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
      
      setSubscription(data);
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    toast({
      title: "Signed out successfully",
      description: "You have been signed out of your account.",
    });
    navigate('/');
  };

  const handleReferrals = () => {
    if (location.pathname === '/') {
      // On landing page, scroll directly to referral program section
      scrollToSection("referral-program");
      setIsOpen(false);
    } else if (!user) {
      toast({
        title: "Sign in required",
        description: "Please sign in to access the referral program.",
        variant: "destructive",
      });
      navigate('/auth');
    } else {
      navigate('/referrals');
    }
  };

  const handleScrollToDemo = () => {
    if (location.pathname === '/') {
      const element = document.getElementById('demo-video');
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate('/#demo-video');
    }
    setIsOpen(false);
  };

  const handleNavClick = (sectionId: string) => {
    if (location.pathname === '/') {
      scrollToSection(sectionId);
    } else {
      navigate(`/#${sectionId}`);
    }
    setIsOpen(false);
  };

  return (
    <nav className="bg-white shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <span className="text-2xl font-bold text-cso-600">CSO Agent</span>
            </div>
          </div>

          {/* Desktop menu */}
          <div className="hidden md:flex items-center space-x-8">
            <button 
              onClick={handleScrollToDemo}
              className="text-gray-700 hover:text-cso-600 transition-colors"
            >
              Demo
            </button>
            <button 
              onClick={() => handleNavClick('features')}
              className="text-gray-700 hover:text-cso-600 transition-colors"
            >
              Features
            </button>
            <button 
              onClick={() => handleNavClick('cost-comparison')}
              className="text-gray-700 hover:text-cso-600 transition-colors"
            >
              Value
            </button>
            <button 
              onClick={() => handleNavClick('pricing')}
              className="text-gray-700 hover:text-cso-600 transition-colors"
            >
              Pricing
            </button>
            <button 
              onClick={handleReferrals}
              className="text-gray-700 hover:text-cso-600 transition-colors"
            >
              Referrals
            </button>
            
            {user && (
              <>
                {(subscription?.subscription_type === 'team' || subscription?.subscription_type === 'enterprise') && (
                  <button 
                    onClick={() => navigate('/organization/dashboard')}
                    className="flex items-center text-gray-700 hover:text-cso-600 transition-colors"
                  >
                    <Building className="h-4 w-4 mr-1" />
                    Organization
                  </button>
                )}
              </>
            )}
            
            {user ? (
              <div className="flex items-center space-x-4">
                <Button onClick={() => navigate('/dashboard')} variant="ghost">
                  <User className="h-4 w-4 mr-2" />
                  Dashboard
                </Button>
                <Button onClick={handleSignOut} variant="ghost">
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </Button>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <Button onClick={() => navigate('/auth')} variant="ghost">
                  Sign In
                </Button>
                <Button onClick={() => navigate('/auth?mode=signup')} className="bg-cso-600 hover:bg-cso-700">
                  Get Started
                </Button>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-700 hover:text-cso-600 focus:outline-none p-2 rounded-lg touch-manipulation"
              style={{ minHeight: '44px', minWidth: '44px' }}
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t shadow-lg">
              <button 
                onClick={handleScrollToDemo}
                className="mobile-nav-item text-gray-700 hover:text-cso-600 hover:bg-gray-50"
              >
                Demo
              </button>
              <button 
                onClick={() => handleNavClick('features')}
                className="mobile-nav-item text-gray-700 hover:text-cso-600 hover:bg-gray-50"
              >
                Features
              </button>
              <button 
                onClick={() => handleNavClick('pricing')}
                className="mobile-nav-item text-gray-700 hover:text-cso-600 hover:bg-gray-50"
              >
                Pricing
              </button>
              <button 
                onClick={handleReferrals}
                className="mobile-nav-item text-gray-700 hover:text-cso-600 hover:bg-gray-50"
              >
                Referrals
              </button>
              
              {user && (subscription?.subscription_type === 'team' || subscription?.subscription_type === 'enterprise') && (
                <button 
                  onClick={() => navigate('/organization/dashboard')}
                  className="flex items-center w-full text-left px-3 py-2 text-gray-700 hover:text-cso-600"
                >
                  <Building className="h-4 w-4 mr-2" />
                  Organization
                </button>
              )}
              
              {user ? (
                <div className="space-y-2">
                  <Button onClick={() => navigate('/dashboard')} className="w-full justify-start" variant="ghost">
                    <User className="h-4 w-4 mr-2" />
                    Dashboard
                  </Button>
                  <Button onClick={handleSignOut} className="w-full justify-start" variant="ghost">
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Button onClick={() => navigate('/auth')} className="w-full" variant="ghost">
                    Sign In
                  </Button>
                  <Button onClick={() => navigate('/auth?mode=signup')} className="w-full bg-cso-600 hover:bg-cso-700">
                    Get Started
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
